/**
 * The kit's minimum-target rule, MEASURED rather than declared.
 *
 * SPEC §7.4 puts control sizing in the kit rather than in a pillar, and the
 * mechanism that reaches the 44px floor is unchanged and stays unchanged: the
 * padding that grows the hit box is cancelled by a negative margin of the same
 * size, so the border box is 44px for hit-testing while the margin box — the
 * only one a flex or grid parent measures — is exactly what it was. That
 * neutrality is verified, as is the fact that the expansion captures no probe
 * point belonging to anything else.
 *
 * What was NOT sound was the input. Each of the five call sites handed over a
 * block size it had been told once, and two of them were wrong by the time they
 * were read: 14 against an 18.59px prose toggle, and 18.3 against a title that
 * had wrapped to 36.59px. A wrong input does not fail safe here — it overshoots,
 * because the expansion is `(44 − declared) / 2` and a declared number smaller
 * than reality buys padding the control does not need. Those two produced 48.59
 * and 62.29px bands against a 44px floor.
 *
 * A line box cannot be derived from a font size without the family's own
 * metrics, and it changes with wrapping, so the only honest input is the box the
 * browser actually laid out. The padding is then read back OFF the element
 * itself, from its own computed style, rather than from a remembered number: the
 * expansion is padding, so a measurement that included it would grow the control
 * a second time, and the version of this that compared against a stored value
 * raced — `ResizeObserver` reports the CONTENT box, and padding does not change
 * it, so the notification that would have corrected the first pass never came
 * and the band settled at twice its size. Reading the live padding makes the
 * measurement idempotent: it computes the same intrinsic height whenever it
 * runs, and running it again never moves the result.
 *
 * It belongs on the control's SEMANTIC ROOT, not on its surface. `Cbutton`
 * paints its preset on the `Ccontainer` inside that root, so padding on the root
 * leaves the visible surface, its hover fill and its press flash untouched; and
 * every root branch — div, anchor and inline span — already activates on a click
 * that lands on its own padding.
 */
"use client";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { CRC_TOUCH_TARGET } from "./cresultcard.tokens";

const NO_EXPANSION = 0;
/** The floor is shared between the two block edges. */
const EDGES = 2;
/**
 * Close enough to be the same band. Layout reports 1/64px, so an observation
 * that finds the control exactly where it left it can still differ in the last
 * decimal, and re-rendering every consumer for that is a render loop dressed up
 * as a measurement.
 */
const SETTLE_TOLERANCE = 0.1;

interface CrcTouchExpansionInput {
  readonly isMobile: boolean;
  /**
   * A ceiling on the expansion, for a control whose band is bounded by a
   * SIBLING CONTROL's — a list row's link has another row's link directly above
   * and below it, and an unbounded expansion there would answer for a target the
   * reader was aiming at somewhere else.
   */
  readonly maxExpansion?: number;
}

export interface CrcTouchExpansion {
  /** Goes on the SAME element the style does: the control's semantic root. */
  readonly measureRoot: (node: HTMLElement | null) => void;
  readonly style: CSSProperties;
}

export function useCrcTouchExpansion({
  isMobile,
  maxExpansion,
}: CrcTouchExpansionInput): CrcTouchExpansion {
  const [root, setRoot] = useState<HTMLElement | null>(null);
  const [expansion, setExpansion] = useState<number>(NO_EXPANSION);
  // What was last published, so an observation that changes nothing does not
  // re-render. A ref, because the observer callback must not be re-subscribed
  // every time the value moves.
  const publishedRef = useRef<number>(NO_EXPANSION);

  const apply = useCallback((next: number): void => {
    if (Math.abs(next - publishedRef.current) <= SETTLE_TOLERANCE) {
      return;
    }
    publishedRef.current = next;
    setExpansion(next);
  }, []);

  const settle = useCallback((): void => {
    if (root == null) {
      return;
    }
    if (!isMobile) {
      apply(NO_EXPANSION);
      return;
    }
    const style = window.getComputedStyle(root);
    const padded =
      (Number.parseFloat(style.paddingTop) || NO_EXPANSION) +
      (Number.parseFloat(style.paddingBottom) || NO_EXPANSION);
    const own = root.getBoundingClientRect().height - padded;
    const wanted = Math.max(NO_EXPANSION, (CRC_TOUCH_TARGET - own) / EDGES);
    apply(maxExpansion == null ? wanted : Math.min(wanted, maxExpansion));
  }, [apply, isMobile, maxExpansion, root]);

  useEffect((): (() => void) | undefined => {
    if (root == null) {
      return undefined;
    }
    settle();
    if (typeof ResizeObserver === "undefined") {
      return undefined;
    }
    // Two observations for one control. A non-replaced inline root — the prose
    // toggle's — has no box a `ResizeObserver` will report, so its own entry
    // only ever arms the callback; what actually changes its line box is the
    // width of the block it sits in, and that is its holder's box.
    const observer = new ResizeObserver(settle);
    observer.observe(root);
    const holder = root.parentElement;
    if (holder != null) {
      observer.observe(holder);
    }
    return (): void => {
      observer.disconnect();
    };
  }, [root, settle]);

  return {
    measureRoot: setRoot,
    style:
      expansion === NO_EXPANSION
        ? {}
        : { paddingBlock: expansion, marginBlock: -expansion },
  };
}
